/**
 * hostForm.js
 *
 * Which of the two host forms this page is (VITRINA_HOST_MODEL.md §2).
 *
 *   ROOT IS AN INDEX   peluquerias.mx/mi-salon — the first path segment is the
 *                      salon's SLUG, and the router mounts at `/{slug}/`.
 *   ROOT IS THE SALON  lasherz.com — the HOST identifies the salon, there is no
 *                      slug at all, and the router mounts at `/`.
 *
 * The edge decides and tells us: it injects `<meta name="route-base">` next to
 * `<meta name="salon-id">` when it stamps the shell. `/` means the host is the
 * salon; `/{slug}/` means the salon lives under a path. Read from the DOM at
 * module init rather than from a `window` global, because the router builds its
 * route table at import time — before any code in main.js has run.
 *
 * Everything that composes an in-salon URL has to go through `salonBasePath`.
 * Deriving those URLs from `route.params.salonSlug` is what broke the root form
 * before this existed: on lasherz.com there is no `salonSlug` param, so every
 * link became `/undefined/...` (René, 2026-08-21).
 */

const META_NAME = 'route-base';

function readRouteBase() {
  const meta = document.querySelector(`meta[name="${META_NAME}"]`);
  return (meta?.getAttribute('content') || '').trim();
}

/** Raw value the edge injected: `/`, `/{slug}/`, or '' when unstamped. */
export const ROUTE_BASE = readRouteBase();

/**
 * True when the host itself IS the salon — a customer's own domain, or the
 * per-salon subdomain. Absent (local dev, or a shell served on a miss) reads as
 * FALSE, which keeps the slug form as the default: it is the one the portfolio
 * serves, and the failure mode of guessing it wrong in the other direction is a
 * salon page that never loads.
 */
export const HOST_IS_SALON = ROUTE_BASE === '/';

/**
 * The path prefix every in-salon link hangs off, for the CURRENT host form.
 *
 * - Root form: '' — so `${base}/book` is `/book`.
 * - Clean slug: `/{slug}`.
 * - Legacy simpleId routes: `/salon/{slug}/{simpleId}`, kept while they soak.
 *
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @returns {string} prefix with no trailing slash ('' in the root form)
 */
export function salonBasePath(route) {
  if (HOST_IS_SALON) return '';
  const { salonSlug, simpleId } = route.params;
  return simpleId ? `/salon/${salonSlug}/${simpleId}` : `/${salonSlug}`;
}

/**
 * Where "home" is for the current host form: '/' at the root, else the base.
 * `salonBasePath` returns '' in the root form, which is not a usable href.
 *
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @returns {string}
 */
export function salonHomePath(route) {
  return salonBasePath(route) || '/';
}
